// Disponibilité hors connexion d'éléments CHOISIS par l'utilisateur (fiche d'équipement, document, site).
// Rien n'est gardé sans action explicite ; tout est effacé à la déconnexion et expire après 7 jours.
// Exception (31/10/2026) : un bon d'intervention OUVERT par l'utilisateur est gardé sur l'appareil (le technicien le remplit en
// sous-sol, sans réseau) — seulement ce bon, que le serveur lui a déjà servi ; même durée, même effacement à la déconnexion.
import { useEffect, useState } from 'react';

const CACHE = 'trame-hors-ligne';
const INDEX = '/__trame/hors-ligne/index';
export const DUREE_JOURS = 7;

export interface ElementHorsLigne { cle: string; type: 'equipement' | 'document' | 'site' | 'journee' | 'bon'; libelle: string; urls: string[]; le: number }

const disponible = () => typeof caches !== 'undefined' && 'serviceWorker' in navigator;

export function enregistrerServiceWorker() {
  if (!disponible() || import.meta.env.DEV) return;
  navigator.serviceWorker.register('/sw.js').catch(() => { /* hors connexion indisponible : l'application fonctionne normalement */ });
}

async function lireIndex(): Promise<ElementHorsLigne[]> {
  if (!disponible()) return [];
  const r = await (await caches.open(CACHE)).match(INDEX);
  if (!r) return [];
  const l = (await r.json()) as ElementHorsLigne[];
  return l.filter((e) => Date.now() - e.le < DUREE_JOURS * 86400_000);
}

async function ecrireIndex(l: ElementHorsLigne[]) {
  await (await caches.open(CACHE)).put(INDEX, new Response(JSON.stringify(l), { headers: { 'x-trame-cache-le': String(Date.now()) } }));
  window.dispatchEvent(new Event('trame:hors-ligne'));
}

/**
 * Télécharge les URL (réponses d'API et fichiers) et les place dans le cache hors connexion. `deja` : réponses JSON déjà en main
 * (la fiche qui vient d'être lue ou enregistrée), gardées telles quelles sans nouvel appel.
 */
export async function rendreDisponible(e: Omit<ElementHorsLigne, 'le'>, deja: Record<string, unknown> = {}) {
  if (!disponible()) throw new Error('Ce navigateur ne permet pas le fonctionnement hors connexion.');
  const c = await caches.open(CACHE);
  // Socle indispensable pour ouvrir l'application hors connexion : identité de session et état des services
  // (rafraîchis à chaque mise hors connexion, effacés à la déconnexion avec le reste).
  for (const url of ['/api/auth/moi', '/api/etat', ...e.urls]) {
    if (Object.prototype.hasOwnProperty.call(deja, url)) {
      await c.put(url, new Response(JSON.stringify(deja[url]), { status: 200, headers: {
        'content-type': 'application/json; charset=utf-8', 'x-trame-cache-le': String(Date.now()) } }));
      continue;
    }
    const r = await fetch(url, { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`Impossible de récupérer un élément (${r.status}).`);
    const corps = await r.blob();
    const entetes = new Headers(r.headers);
    entetes.set('x-trame-cache-le', String(Date.now()));
    await c.put(url, new Response(corps, { status: 200, headers: entetes }));
  }
  const l = (await lireIndex()).filter((x) => x.cle !== e.cle);
  await ecrireIndex([...l, { ...e, le: Date.now() }]);
}

export async function retirer(cle: string) {
  const l = await lireIndex();
  const el = l.find((x) => x.cle === cle);
  if (el) { const c = await caches.open(CACHE); for (const u of el.urls) await c.delete(u); }
  await ecrireIndex(l.filter((x) => x.cle !== cle));
}

/** Clés des saisies gardées sur l'appareil : brouillons génériques et brouillons de diagnostic (trame.intervention.<id>.brouillon). */
export const estCleBrouillon = (k: string) =>
  k.startsWith('trame.brouillon.') || (k.startsWith('trame.intervention.') && k.endsWith('.brouillon'));

/** Nombre de saisies encore sur l'appareil, pas encore envoyées au serveur (une saisie envoyée efface son brouillon). */
export function saisiesNonEnvoyees(): number {
  try { return Object.keys(localStorage).filter(estCleBrouillon).length; } catch { return 0; }
}

/** À la déconnexion : rien ne reste sur l'appareil (cache des fiches ET brouillons de saisie). */
export async function viderHorsLigne() {
  try { for (const k of Object.keys(localStorage)) if (estCleBrouillon(k)) localStorage.removeItem(k); } catch { /* stockage indisponible */ }
  if (!disponible()) return;
  await caches.delete(CACHE);
  navigator.serviceWorker.controller?.postMessage({ type: 'vider' });
  window.dispatchEvent(new Event('trame:hors-ligne'));
}

/** Message de confirmation avant une déconnexion qui ferait perdre des saisies non envoyées (null si rien en attente). */
export function avertissementDeconnexion(): string | null {
  const n = saisiesNonEnvoyees();
  if (!n) return null;
  return `${n} saisie${n > 1 ? 's' : ''} n’${n > 1 ? 'ont' : 'a'} pas encore été envoyée${n > 1 ? 's' : ''} (réseau absent au moment de la saisie). `
    + 'En vous déconnectant, elle' + (n > 1 ? 's seront effacées' : ' sera effacée') + ' de cet appareil. Retrouvez du réseau et ouvrez l’intervention pour l’envoyer, ou confirmez.';
}

export function useHorsLigne() {
  const [liste, setListe] = useState<ElementHorsLigne[]>([]);
  useEffect(() => {
    const maj = () => { lireIndex().then(setListe).catch(() => setListe([])); };
    maj();
    window.addEventListener('trame:hors-ligne', maj);
    return () => window.removeEventListener('trame:hors-ligne', maj);
  }, []);
  return { liste, possible: disponible() && !import.meta.env.DEV, estDisponible: (cle: string) => liste.some((x) => x.cle === cle) };
}
